/*
 * $Id:: index.js 2021/12/13 12:44 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { globals as globalsSlice } from '../../app/globalsSlice';
import Card from '../../components/Card';
import CorrespondenceGrid from '../../components/CorrespondenceGrid';
import CorrespondenceTable from '../../components/CorrespondenceTable';
import ApplicationFilters from '../../components/Filters/ApplicationFilters';
import PageTitle from '../../components/PageTitle';
import CustomPagination from '../../components/Pagination/CustomPagination';
import WarningModal from '../../components/WarningModal';
import { t } from '../../providers';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME as DETAILS_SLICE_NAME } from '../CorrespondenceDetails/constants';
import { SLICE_NAME } from './constants';
import { list, loading as loadingSlice, error as errorSlice, listParameters } from './correspondenceSlice';
import filterItems from './filterItems';
import { correspondenceParserGrid, correspondenceParserList, parseRequestData } from './utils';

const useStyles = makeStyles((_theme) => ({
    container: {
        width: '100%',
        height: '100%',
        maxWidth: 'calc(100% - 236px)',
        '@media (max-width:1100px)': {
            minWidth: '77%',
            maxWidth: 'unset',
        },
    },
    titleAndSwitch: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width:1100px)': {
            display: 'block',
        },
    },
    switch: {
        display: 'flex',
        '@media (max-width:1100px)': {
            float: 'right',
        },
    },
    switchGrid: {
        '&.disabled': {
            cursor: 'not-allowed',
            '& .MuiButtonBase-root': {
                cursor: 'not-allowed',
            },
        },
        '& .MuiFormControlLabel-label': {
            color: '#272833',
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '29px',
            paddingLeft: '8px',
        },
    },
    tabs: {
        '& .MuiTab-root': {
            minWidth: '100px',
        },
    },
    tabContainer: {
        width: '100%',
    },
    filtersGrid: {
        marginTop: '30px',
        marginRight: '20px',
    },
    cardTable: {
        marginRight: '20px',
        '@media (max-width:960px)': {
            marginRight: 'unset',
        },
    },
}));

const ALL_IP_RIGHTS = 'all';

const ASC = 'ASC';
const DESC = 'DESC';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;

const CorrespondencePage = () => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const loading = useSelector(loadingSlice);
    const error = useSelector(errorSlice);
    const { globals } = useSelector(globalsSlice) || {};
    const correspondence = useSelector(list);
    const corrParameters = useSelector(listParameters);
    const me = useSelector(meSlice);
    const isTablet = useMediaQuery('(max-width:960px)');

    const { totalResults: totalElements, pageSize: size, pageNumber: number } = correspondence || {};
    const maxReplies = globals?.application['correspondence.maxReplies'];

    const [userType, setUserType] = useState('');
    const [dataParams, setDataParams] = useState({});
    const [tabs, setTabs] = useState([]);
    const [sortableFieldsGrid, setSortableFieldsGrid] = useState([]);
    const [correspondenceGrid, setCorrespondenceGrid] = useState([]);
    const [correspondenceList, setCorrespondenceList] = useState([]);
    const [errorDialog, setErrorDialog] = useState({
        visibility: false,
        message: '',
        onConfirmation: () => {},
        confirmButtonText: '',
        cancelButtonText: '',
        onCancel: () => {},
        id: '',
    });
    const [readOnly, setReadOnly] = useState(false);
    const [requestData, setRequestData] = useState({});

    const { selectedTab } = dataParams;

    const updatePersistentParameters = (params) => dispatch({ type: `${SLICE_NAME}/updateCorrParameters`, payload: params });

    const handleCloseDialog = () => {
        setErrorDialog({
            visibility: false,
            message: '',
            onConfirmation: () => {},
            confirmButtonText: '',
            cancelButtonText: '',
            onCancel: () => {},
            id: '',
        });
    };

    const handleDeleteDraft = (draftId) => {
        setErrorDialog({
            ...errorDialog,
            visibility: true,
            message: t('delete.draft.correspondence.confirmation'),
            onConfirmation: () => deleteDraft(draftId),
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel'),
            onCancel: handleCloseDialog,
        });
    };

    const deleteDraft = (draftId) => {
        dispatch({
            type: `${SLICE_NAME}/requestDraftCorrespondencePageDelete`,
            payload: { draftId, actionParams: requestData },
        });
        handleCloseDialog();
    };

    const downloadIncomingCorrespondence = (correspondenceId) =>
        dispatch({ type: `${DETAILS_SLICE_NAME}/downloadIncomingMessage`, payload: { corrId: correspondenceId } });

    const downloadOutgoingCorrespondence = (correspondenceId) =>
        dispatch({ type: `${SLICE_NAME}/downloadOutgoingDraft`, payload: { corrId: correspondenceId } });

    const setSorting = (mobile, column, type) => {
        let sortColumn, sortType;
        if (mobile) {
            sortColumn = column ? column : dataParams.sort;
            sortType = type ? type : dataParams.sortType;
        } else {
            sortColumn = column;
            const isSameKey = sortColumn === dataParams.sort;
            const oppositeSort = dataParams.sortType === ASC ? DESC : ASC;
            sortType = !isSameKey ? ASC : oppositeSort;
        }
        updatePersistentParameters({
            ...corrParameters,
            requestPage: DEFAULT_PAGE_NUMBER,
            size: size,
            sort: sortColumn,
            sortType: sortType,
        });
    };

    const handleChangePage = (event, newPage) => {
        event.preventDefault();
        if (newPage !== number) {
            updatePersistentParameters({
                ...corrParameters,
                requestPage: newPage,
                size: size,
            });
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = event.target.value;
        if (size !== pageSize) {
            updatePersistentParameters({
                ...corrParameters,
                requestPage: 0,
                size: pageSize,
            });
        }
    };

    const handleFilters = (data) => {
        setDataParams(data);
        updatePersistentParameters({
            ...corrParameters,
            requestPage: 0,
            filters: data.filters,
            searchQuery: data.searchQuery,
            filtersChips: data.filtersChips,
        });
    };

    const clearFilters = () => {
        setDataParams({ ...dataParams, filters: {}, filtersChips: {} });
        updatePersistentParameters({
            ...corrParameters,
            requestPage: 0,
            filters: {},
            filtersChips: {},
        });
    };

    const handleTabChange = (event, value) => {
        event.preventDefault();
        setDataParams({ ...dataParams, searchQuery: '', filters: {}, filtersChips: {} });
        updatePersistentParameters({
            ...corrParameters,
            requestPage: 0,
            selectedTab: tabs[value],
            searchQuery: '',
            filters: {},
            filtersChips: {},
        });
    };

    const handleNavigation = (params) => history.push(params);

    const handleToggleImportant = (event, message) => {
        event.preventDefault();
        console.log('onChangeValue ', event.target.checked, event, message);
        console.log('correspondence', correspondence);
        dispatch({ type: `${SLICE_NAME}/updateMessageImportance`, payload: { messageId: message.id, important: event.target.checked } });
    };

    const handleMarkAsUnread = (message) => {
        console.log('correspondence', correspondence);
        dispatch({ type: `${SLICE_NAME}/updateMessageStatus`, payload: { messageId: message.id, messageStatus: 'NEW' } });
    };

    const canAccess = (authorities) => 
        authorities && (
            authorities.includes('ROLE_CORRESPONDENCE_READ_WRITE') || authorities.includes('ROLE_CORRESPONDENCE_READ_ONLY')
        );

    const canReadOnly = (authorities) => {
        let value = !authorities.includes('ROLE_CORRESPONDENCE_READ_WRITE') && authorities.includes('ROLE_CORRESPONDENCE_READ_ONLY');
        return value;
    }

    useEffect(() => {
        const { authorities, personType, impersonatedUser } = me;
        setUserType(personType);
        const allowedTabs = canAccess(authorities) ? [ALL_IP_RIGHTS] : [];
        if ( canAccess(authorities) && allowedTabs.length > 0) {
            setTabs(allowedTabs); //set tabs depending on tabs allowed based on authorities role
            if (!corrParameters?.selectedTab)
                updatePersistentParameters({
                    ...corrParameters,
                    selectedTab: allowedTabs[0],
                });
            if (canReadOnly(authorities) || !!impersonatedUser) {
                setReadOnly(true);
            }
        } else {
            history.push('/');
        }
    }, [me, history]);

    useEffect(() => {
        const { sort, sortType, requestPage, size, selectedTab, searchQuery, filters, filtersChips } = corrParameters;
        setDataParams({ sort, sortType, requestPage, size, selectedTab, searchQuery, filters, filtersChips });
    }, [corrParameters]);

    useEffect(() => {
        if (tabs.length > 0) {
            const params = parseRequestData(corrParameters);
            setRequestData({ params });
        }
    }, [dispatch, tabs, corrParameters]);

    useEffect(() => {
        if (!!Object.keys(requestData).length) {
            dispatch({ type: `${SLICE_NAME}/requestCorrespondence` });
            dispatch({ type: `${SLICE_NAME}/requestNewCorrespondencesCount` });
        }
    }, [requestData]);

    useEffect(() => {
        if (!!error) {
            const messageModal =
                error.code === 403 ? t(error.description, { username: error.details.username, message: error.details.message }) : t(error);
            setErrorDialog({
                ...errorDialog,
                visibility: true,
                message: messageModal,
                onConfirmation: handleCloseDialog,
                confirmButtonText: t('ok'),
                cancelButtonText: '',
                onCancel: () => {},
            });
        }
    }, [error]);

    useEffect(() => {
        if (tabs.length > 0) {
            if (isTablet) {
                const { correspondenceGrid, sortableFields } = correspondenceParserGrid(
                    correspondence || [],
                    handleNavigation,
                    handleDeleteDraft,
                    handleMarkAsUnread,
                    readOnly,
                    maxReplies,
                    downloadIncomingCorrespondence,
                    downloadOutgoingCorrespondence
                );
                setCorrespondenceGrid(correspondenceGrid);
                setSortableFieldsGrid(sortableFields);
            } else {
                const correspondenceList = correspondenceParserList(
                    correspondence || [],
                    handleNavigation,
                    handleDeleteDraft,
                    handleToggleImportant,
                    handleMarkAsUnread,
                    readOnly,
                    maxReplies,
                    downloadIncomingCorrespondence,
                    downloadOutgoingCorrespondence,
                    true
                );
                setCorrespondenceList(correspondenceList);
            }
        }
    }, [correspondence, isTablet, selectedTab, tabs, userType, readOnly]);

    const correspondenceTable = !loading ? (
        <CorrespondenceTable
            setSorting={setSorting}
            sortBy={dataParams.sort}
            sortType={dataParams.sortType}
            correspondence={correspondenceList}
            tableId={'correspondence'}
        />
    ) : (
        <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
        </Box>
    );
    const corrGrid = !loading ? (
        <CorrespondenceGrid
            sortableFields={sortableFieldsGrid}
            setSorting={setSorting}
            sortBy={dataParams.sort}
            sortType={dataParams.sortType}
            correspondence={correspondenceGrid}
            gridId={'correspondence'}
        />
    ) : (
        <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
        </Box>
    );

    const customPagination = (
        <CustomPagination
            defaultPageSize={DEFAULT_PAGE_SIZE}
            totalElements={totalElements}
            size={size || DEFAULT_PAGE_SIZE}
            page={number || DEFAULT_PAGE_NUMBER}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            paginationId={'correspondence'}
        />
    );
    const filtersSection = (
        <ApplicationFilters
            applicationType={selectedTab}
            filtersValues={dataParams}
            setFiltersValues={handleFilters}
            clearFilters={clearFilters}
            exportApplications={null}
            filterItems={filterItems}
            datesOptions={'datesCorr'}
        />
    );

    return (
        <div className={classes.container}>
            {/* title */}
            <div className={classes.titleAndSwitch}>
                <div>
                    <PageTitle title={t('correspondence')} details={t('correspondence.description')} />
                </div>
            </div>

            {/* tabs */}
            {tabs.indexOf(selectedTab) > -1 && (
                <Tabs className={classes.tabs} value={tabs.indexOf(selectedTab)} onChange={handleTabChange} textColor="primary">
                    {tabs.map((tab, index) => (
                        <Tab id={`tab-${tab}-${index}`} key={`tab-${index}`} label={t(tab + '.tab.title')} />
                    ))}
                </Tabs>
            )}

            {/* data display */}
            <div className={classes.cardTable}>
                {isTablet ? (
                    <React.Fragment>
                        {filtersSection}
                        {corrGrid}
                        {customPagination}
                    </React.Fragment>
                ) : (
                    <Card canEdit={false}>
                        {filtersSection}
                        {correspondenceTable}
                        {customPagination}
                    </Card>
                )}
            </div>
            {/* error dialog */}
            <WarningModal
                visibility={errorDialog.visibility}
                message={errorDialog.message}
                onConfirmation={errorDialog.onConfirmation}
                confirmButtonText={errorDialog.confirmButtonText}
                cancelButtonText={errorDialog.cancelButtonText}
                onCancel={errorDialog.onCancel}
            />
        </div>
    );
};

export default CorrespondencePage;
